import useIntegrationSettings from "common/datahooks/useIntegrationSettings";
import { typedObjectEntries } from "common/helpers/utils";
import {
  ContentContainer,
  ContentHeader,
  PageTitle,
} from "common/layouts/WorkspaceLayout";
import type {
  InstalledIntegrationSettingsItem,
  InstantlyIntegration,
  Rb2bIntegration as Rb2bIntegrationType,
  SmartleadIntegration,
} from "common/types";

import { INTEGRATIONS_MAP } from "../constants";
import CrmIntegrationSettings from "./CrmIntegrationSettings";
import EmailOutreachConnect from "./EmailOutreachConnect";
import EmailOutreachSettings from "./EmailOutreachSettings";
import IntegrationItem from "./IntegrationItem";
import Rb2bIntegration from "./Rb2bIntegration";

import type { JSX } from "react";

export default function IntegrationsList() {
  const { integrationSettings } = useIntegrationSettings();

  return (
    <ContentContainer>
      <ContentHeader>
        <PageTitle />
      </ContentHeader>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3 p-4">
        {typedObjectEntries(INTEGRATIONS_MAP).map(
          ([key, { icon, title, description, ...rest }]) => {
            let props:
              | { redirectUrl: string }
              | { settingsComponent: JSX.Element };

            if ("redirectUrl" in rest) {
              props = { redirectUrl: rest.redirectUrl };
            } else {
              if (!integrationSettings) {
                return (
                  <IntegrationItem
                    key={title}
                    icon={icon}
                    type={key}
                    description={description}
                    isLoading
                  />
                );
              }

              const settings =
                integrationSettings[key as keyof typeof integrationSettings];

              if (key === "instantly" || key === "smartlead") {
                let settingsComponent = <EmailOutreachConnect type={key} />;

                if (settings) {
                  settingsComponent =
                    key === "instantly" ? (
                      <EmailOutreachSettings
                        type="instantly"
                        workspaceName={
                          (settings as InstantlyIntegration).workspace_name
                        }
                      />
                    ) : (
                      <EmailOutreachSettings
                        type="smartlead"
                        apiKey={(settings as SmartleadIntegration).api_key}
                      />
                    );
                }

                props = { settingsComponent };
              } else if (key === "hubspot" || key === "pipedrive") {
                props =
                  "install_url" in settings
                    ? { redirectUrl: settings.install_url }
                    : {
                        settingsComponent: (
                          <CrmIntegrationSettings
                            settings={
                              settings as InstalledIntegrationSettingsItem
                            }
                            crmType={key}
                          />
                        ),
                      };
              } else {
                props = {
                  settingsComponent: (
                    <Rb2bIntegration
                      settings={settings as Rb2bIntegrationType}
                    />
                  ),
                };
              }
            }

            return (
              <IntegrationItem
                key={key}
                type={key}
                icon={icon}
                description={description}
                {...props}
              />
            );
          },
        )}
      </div>
    </ContentContainer>
  );
}
